import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { api } from "@/lib/auth";
import { ArrowRight, Zap, TrendingUp, Calendar, Trophy, Lock } from "lucide-react";
import OnboardingModal from "@/components/OnboardingModal";
import ReferralBanner from "@/components/ReferralBanner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function Dashboard() {
    const { t } = useLang();
    const { user, tokens, plan } = useAuth();
    const isUnlimitedPlan = plan === "studio" || plan === "studio_member";
    const [sessions, setSessions] = useState([]);
    const [showOnboarding, setShowOnboarding] = useState(false);

    useEffect(() => {
        if (user && user.onboarded === false) {
            setShowOnboarding(true);
        }
    }, [user]);
    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/sessions").then((r) => {
            setSessions(r.data.sessions || []);
            setLoading(false);
        }).catch(() => setLoading(false));
        api.get("/achievements").then((r) => setAchievements(r.data.achievements || [])).catch(() => {});
    }, []);

    const completed = sessions.filter((s) => s.scores);
    const avg = completed.length
        ? Math.round(completed.reduce((a, s) => a + (s.scores?.overall || 0), 0) / completed.length)
        : 0;

    const progressData = [...completed]
        .sort((a, b) => new Date(a.started_at) - new Date(b.started_at))
        .map((s, i) => ({
            index: i + 1,
            date: new Date(s.started_at).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" }),
            score: s.scores.overall,
        }));

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <div className="mb-12">
                    <div className="mono-label mb-2">/ DASHBOARD</div>
                    <h1 className="font-display text-5xl md:text-6xl tracking-tighter">
                        {t("dash.welcome")} <span className="text-brand">{user?.name}.</span>
                    </h1>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100 border border-slate-200 mb-12">
                    <StatCell label={t("dash.tokens_left")} value={isUnlimitedPlan ? "∞" : tokens} accent icon={<Zap size={14} />} testid="stat-tokens" />
                    <StatCell label={t("dash.sessions_done")} value={completed.length} icon={<Calendar size={14} />} testid="stat-sessions" />
                    <StatCell label={t("dash.avg_score")} value={avg} icon={<TrendingUp size={14} />} suffix="/100" testid="stat-avg-score" />
                    <StatCell label={t("dash.plan")} value={plan?.toUpperCase() || "FREE"} testid="stat-plan" />
                </div>

                <div className="flex flex-wrap gap-3 mb-16">
                    <Link to="/practice" className="btn-primary" data-testid="start-session-btn">
                        {t("dash.start_session")} <ArrowRight size={16} />
                    </Link>
                    {tokens === 0 && (
                        <Link to="/pricing" className="btn-ghost" data-testid="dash-upgrade-cta">
                            {t("dash.upgrade_cta")}
                        </Link>
                    )}
                </div>

                <ReferralBanner />

                {/* Progress over time */}
                {progressData.length >= 2 && (
                    <div className="mb-16" data-testid="progress-chart-section">
                        <h2 className="font-display text-3xl mb-6 tracking-tight">{t("dash.progress_title")}</h2>
                        <div className="card-surface p-6">
                            <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={progressData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                                    <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={32} />
                                    <Tooltip
                                        contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 13 }}
                                        formatter={(value) => [`${value}/100`, t("dash.avg_score")]}
                                    />
                                    <Line type="monotone" dataKey="score" stroke="#00AEEF" strokeWidth={2.5} dot={{ r: 4, fill: "#00AEEF" }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Achievements */}
                {achievements.length > 0 && (
                    <div className="mb-16">
                        <h2 className="font-display text-3xl mb-6 tracking-tight">{t("achievements.title")}</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="achievements-grid">
                            {achievements.map((a) => (
                                <div
                                    key={a.id}
                                    className={`card-surface p-4 ${a.unlocked ? "border-brand/40" : "opacity-50"}`}
                                    data-testid={`achievement-${a.id}`}
                                >
                                    {a.unlocked ? (
                                        <Trophy size={16} className="text-brand mb-2" />
                                    ) : (
                                        <Lock size={16} className="text-slate-400 mb-2" />
                                    )}
                                    <div className="text-sm font-medium mb-1">{a.title}</div>
                                    <div className="mono-label text-slate-400">
                                        +{a.reward} {a.reward === 1 ? t("achievements.reward") : t("achievements.rewards")}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* History */}
                <div>
                    <h2 className="font-display text-3xl mb-6 tracking-tight">{t("dash.history")}</h2>
                    {loading && <p className="text-slate-500">{t("common.loading")}</p>}
                    {!loading && sessions.length === 0 && (
                        <p className="text-slate-500" data-testid="no-sessions-msg">{t("dash.no_sessions")}</p>
                    )}
                    {sessions.length > 0 && (
                        <div className="border border-slate-200 divide-y divide-slate-200" data-testid="sessions-list">
                            {sessions.map((s) => (
                                <Link
                                    key={s.id}
                                    to={`/session/${s.id}`}
                                    className="grid grid-cols-12 gap-4 px-6 py-5 hover:bg-slate-50 transition-colors items-center"
                                    data-testid={`session-row-${s.id}`}
                                >
                                    <div className="col-span-3 mono-label">{new Date(s.started_at).toLocaleString()}</div>
                                    <div className="col-span-3 font-display text-lg">{t(`practice.scenarios.${s.scenario}`)}</div>
                                    <div className="col-span-2 mono-label text-slate-500">{s.language.toUpperCase()}</div>
                                    <div className="col-span-2">
                                        {s.scores ? (
                                            <span className="font-mono text-brand">{s.scores.overall}/100</span>
                                        ) : (
                                            <span className="mono-label text-slate-400">—</span>
                                        )}
                                    </div>
                                    <div className="col-span-2 text-right text-sm text-slate-500 hover:text-brand">
                                        {t("dash.view")} →
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

function StatCell({ label, value, accent, suffix, icon, testid }) {
    return (
        <div className={`bg-ink p-6 lg:p-8 ${accent ? "border-l-2 border-l-brand" : ""}`} data-testid={testid}>
            <div className="mono-label mb-4 flex items-center gap-2">
                {icon} {label}
            </div>
            <div className={`font-display text-4xl md:text-5xl ${accent ? "text-brand" : ""}`}>
                {value}
                {suffix && <span className="text-base font-mono text-slate-400 ml-1">{suffix}</span>}
            </div>
        </div>
    );
}
